/**
 * RelationshipGraphViewer Component
 * Feature: 080-critique-sharing
 *
 * Displays a user's engagement graph showing all their connections.
 * Admin-only component for debugging and understanding user relationships.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useUserEngagementGraph,
  ConnectionSummary,
  formatInteractionType,
} from '../../graphql/hooks/useEngagementGraphGraphQL';

interface RelationshipGraphViewerProps {
  userId: string;
  onSelectConnection?: (connection: ConnectionSummary) => void;
}

export function RelationshipGraphViewer({
  userId,
  onSelectConnection,
}: RelationshipGraphViewerProps) {
  const { data, isLoading, isError, error, refetch } = useUserEngagementGraph(userId);
  const [filter, setFilter] = useState<'all' | 'registered' | 'pending'>('all');
  const [sortBy, setSortBy] = useState<'recent' | 'count'>('recent');

  if (isLoading) {
    return (
      <div className="bg-surface-1 rounded-lg border border-border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-surface-2 rounded w-1/3"></div>
          <div className="grid grid-cols-3 gap-4">
            <div className="h-20 bg-surface-2 rounded"></div>
            <div className="h-20 bg-surface-2 rounded"></div>
            <div className="h-20 bg-surface-2 rounded"></div>
          </div>
          <div className="h-40 bg-surface-2 rounded"></div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-surface-1 rounded-lg border border-border p-6">
        <div className="text-center text-red-500">
          <p className="font-medium">Failed to load engagement graph</p>
          <p className="text-sm mt-1">{error?.message || 'Unknown error'}</p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-surface-1 rounded-lg border border-border p-6">
        <p className="text-muted text-center">No engagement data found</p>
      </div>
    );
  }

  // Filter connections
  let filteredConnections = data.connections;
  if (filter === 'registered') {
    filteredConnections = data.connections.filter((c) => !c.isPending);
  } else if (filter === 'pending') {
    filteredConnections = data.connections.filter((c) => c.isPending);
  }

  // Sort connections
  if (sortBy === 'count') {
    filteredConnections = [...filteredConnections].sort(
      (a, b) => b.outboundCount + b.inboundCount - (a.outboundCount + a.inboundCount)
    );
  }
  // 'recent' is the default sort from the API

  return (
    <div className="space-y-6">
      {/* Header with stats */}
      <div className="bg-surface-1 rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">
            Engagement Graph: {data.username}
          </h3>
          <span className="text-sm text-muted">
            {data.connectionCount} total connections
          </span>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard label="Critiques Given" value={data.stats.critiquesGiven} />
          <StatCard label="Critiques Received" value={data.stats.critiquesReceived} />
          <StatCard label="Replies" value={data.stats.critiqueReplies} />
          <StatCard label="Shares Sent" value={data.stats.sharesSent} />
          <StatCard label="Shares Received" value={data.stats.sharesReceived} />
          <StatCard label="Photos Curated" value={data.stats.photosCurated} />
        </div>

        {/* Connection counts */}
        <div className="mt-4 flex gap-4 text-sm">
          <span className="text-muted">
            <span className="text-foreground font-medium">{data.registeredConnectionCount}</span>{' '}
            registered users
          </span>
          <span className="text-muted">
            <span className="text-foreground font-medium">{data.pendingConnectionCount}</span>{' '}
            pending (email-only)
          </span>
        </div>
      </div>

      {/* Filters and connections list */}
      <div className="bg-surface-1 rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-md font-medium text-foreground">Connections</h4>
          <div className="flex gap-2">
            {/* Filter buttons */}
            <div className="flex rounded-md border border-border overflow-hidden">
              <FilterButton
                active={filter === 'all'}
                onClick={() => setFilter('all')}
                label={`All (${data.connectionCount})`}
              />
              <FilterButton
                active={filter === 'registered'}
                onClick={() => setFilter('registered')}
                label={`Users (${data.registeredConnectionCount})`}
              />
              <FilterButton
                active={filter === 'pending'}
                onClick={() => setFilter('pending')}
                label={`Pending (${data.pendingConnectionCount})`}
              />
            </div>

            {/* Sort dropdown */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'recent' | 'count')}
              className="px-3 py-1.5 bg-surface-2 border border-border rounded-md text-sm text-foreground"
            >
              <option value="recent">Most Recent</option>
              <option value="count">Most Interactions</option>
            </select>
          </div>
        </div>

        {/* Connections table */}
        {filteredConnections.length === 0 ? (
          <p className="text-muted text-center py-8">No connections match the filter</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3 text-sm font-medium text-muted">User</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-muted">Status</th>
                  <th className="text-right py-2 px-3 text-sm font-medium text-muted">Outbound</th>
                  <th className="text-right py-2 px-3 text-sm font-medium text-muted">Inbound</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-muted">Last Activity</th>
                  <th className="text-right py-2 px-3 text-sm font-medium text-muted"></th>
                </tr>
              </thead>
              <tbody>
                {filteredConnections.map((connection, index) => (
                  <ConnectionRow
                    key={connection.userId || connection.email || index}
                    connection={connection}
                    onSelect={onSelectConnection}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface StatCardProps {
  label: string;
  value: number;
}

function StatCard({ label, value }: StatCardProps) {
  return (
    <div className="bg-surface-2 rounded-lg p-3 text-center">
      <div className="text-xl font-bold text-foreground">{value}</div>
      <div className="text-xs text-muted">{label}</div>
    </div>
  );
}

interface FilterButtonProps {
  active: boolean;
  onClick: () => void;
  label: string;
}

function FilterButton({ active, onClick, label }: FilterButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-sm transition-colors ${
        active
          ? 'bg-primary text-primary-foreground'
          : 'bg-surface-2 text-muted hover:text-foreground'
      }`}
    >
      {label}
    </button>
  );
}

interface ConnectionRowProps {
  connection: ConnectionSummary;
  onSelect?: (connection: ConnectionSummary) => void;
}

function ConnectionRow({ connection, onSelect }: ConnectionRowProps) {
  const lastActivity = new Date(connection.lastInteractionAt);
  const formattedDate = lastActivity.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: lastActivity.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });

  return (
    <tr className="border-b border-border/50 hover:bg-surface-2/50">
      <td className="py-3 px-3">
        <div className="flex items-center gap-3">
          {connection.avatarUrl ? (
            <img
              src={connection.avatarUrl}
              alt={connection.username || 'User'}
              className="w-8 h-8 rounded-full"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-surface-2 flex items-center justify-center text-muted text-sm">
              {connection.isPending ? '@' : (connection.username?.[0]?.toUpperCase() || '?')}
            </div>
          )}
          <div>
            {connection.isPending ? (
              <div className="text-foreground font-medium">{connection.email}</div>
            ) : connection.userId ? (
              <Link
                to={`/admin/users/${connection.userId}/relationships`}
                className="text-foreground font-medium hover:text-primary transition-colors"
              >
                {connection.username}
              </Link>
            ) : (
              <div className="text-foreground font-medium">{connection.username}</div>
            )}
            {!connection.isPending && connection.displayName && (
              <div className="text-xs text-muted">{connection.displayName}</div>
            )}
          </div>
        </div>
      </td>
      <td className="py-3 px-3">
        {connection.isPending ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/20 text-yellow-500">
            Pending
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-500">
            Registered
          </span>
        )}
      </td>
      <td className="py-3 px-3 text-right">
        <span className="text-foreground font-medium">{connection.outboundCount}</span>
      </td>
      <td className="py-3 px-3 text-right">
        <span className="text-foreground font-medium">{connection.inboundCount}</span>
      </td>
      <td className="py-3 px-3 text-muted text-sm">{formattedDate}</td>
      <td className="py-3 px-3 text-right">
        <button
          onClick={() => onSelect?.(connection)}
          className="text-primary hover:text-primary/80 text-sm"
        >
          Details
        </button>
      </td>
    </tr>
  );
}

export { formatInteractionType };
